// =============================================================================
// src/features/explore/explore-tse-zone-layer.ts — Camada Leaflet "Zonas
//   eleitorais (TSE)" do Explorar (pedido 2026-10-13: contornos fieis as zonas
//   eleitorais).
//
// Delegado do ExploreMap (explore-map.ts ja passa de 500 linhas): o mapa so chama
//   setVisible(on). Comportamento:
//   - o GeoJSON (~690 KB gzip) so e baixado no 1o "ligar" (memoizado);
//   - desenha contorno tracejado + rotulo "Nª ZE" num pane proprio acima dos
//     municipios/zonas (a troca de nivel por zoom re-adiciona aquelas camadas e
//     as poria por cima no overlayPane);
//   - rotulo so aparece quando a zona tem largura >= TSE_ZONE_LABEL_MIN_PX na tela
//     (no AM inteiro as 13 zonas de Manaus se sobreporiam);
//   - clique abre o popup com eleitorado/resultados (explore-tse-zone-card);
//   - porta opcional de RESULTADO (modo Eleicoes 2026): pinta cada zona pelo mais
//     votado do cargo ativo e poe o bloco 2026 no topo do popup.
//
// API Leaflet usada (L.geoJSON/divIcon/marker/layerGroup/popup, map.createPane,
//   latLngToContainerPoint) e a mesma ja usada no explore-map/vote-layer —
//   Leaflet 1.9.4, tipada por @types/leaflet 1.9.20 (tsc trava o contrato).
// =============================================================================

import L from 'leaflet';
import type { GeoJSON as LeafletGeoJSON, LayerGroup, LeafletMouseEvent, Map as LeafletMap, Marker, Path, PathOptions } from 'leaflet';
import type { HexColor } from '../../app/types';
import { polygonCentroidOf } from './explore-geo';
import { buildTseZonePopupHtml } from './explore-tse-zone-card';
import type { TseZoneCollection, TseZoneFeature } from './explore-tse-zone-types';

/**
 * Altura maxima (px) do popup da zona: com o bloco 2026 + 2024 + 2022 ele passava da
 * tela e o topo sumia sob os chips (visto no smoke de 2026-10-13) — rola por dentro.
 */
export const TSE_ZONE_POPUP_MAX_HEIGHT = 420;

/**
 * Opcoes dos popups altos do Explorar (zona TSE, local e ranking 2026): rolagem
 * interna + auto-pan que respeita a barra de busca/chips no topo (~170 px) — sem
 * isso o Leaflet so afastava 5 px da borda e o topo do popup ficava sob os chips.
 */
export const EXPLORE_TALL_POPUP_OPTIONS = {
  className: 'exp-vote-popup', maxWidth: 320, maxHeight: TSE_ZONE_POPUP_MAX_HEIGHT, autoPan: true, autoPanPaddingTopLeft: [16, 170],
} as const satisfies L.PopupOptions;

/** Pane dedicado (zIndex entre overlayPane=400 e markerPane=600). */
export const TSE_ZONE_PANE = 'expTseZonesPane';
const TSE_ZONE_PANE_Z_INDEX = '450';
/** Largura minima (px) da zona na tela para exibir o rotulo. */
export const TSE_ZONE_LABEL_MIN_PX = 70;

/** Estilo do contorno em repouso e em hover. */
export const TSE_ZONE_STYLE = { color: '#1E3A5F', weight: 1.6, dashArray: '5 4', fillColor: '#185FA5', fillOpacity: 0.04 } as const;
const TSE_ZONE_HOVER_STYLE = { weight: 3, fillOpacity: 0.12 } as const;

/** Opacidade do preenchimento quando a zona e pintada pelo resultado (modo 2026). */
const TSE_ZONE_RESULT_FILL_OPACITY = 0.3;

/** Resultado por zona injetado pelo modo 2026 (cor do mais votado + bloco do popup). */
export interface TseZoneResultPort {
  fillColor(nrZona: number): HexColor | null;
  popupBlock(nrZona: number): string;
}

/** Carrega o asset de zonas (injetado — fetch em producao, fake nos testes). */
export type TseZoneLoader = () => Promise<TseZoneCollection>;

/** Rotulo de zona + bounds para decidir a visibilidade por zoom. */
interface TseZoneLabel {
  readonly marker: Marker;
  readonly bounds: L.LatLngBounds;
}

/** Camada de zonas eleitorais do TSE (liga/desliga sob demanda). */
export class ExploreTseZoneLayer {
  private loading: Promise<TseZoneCollection> | null = null;
  private outlineLayer: LeafletGeoJSON | null = null;
  private readonly labelGroup: LayerGroup = L.layerGroup();
  private labels: TseZoneLabel[] = [];
  private visible = false;
  private resultPort: TseZoneResultPort | null = null;
  private readonly onZoomEnd = (): void => this.refreshLabels();

  constructor(private readonly map: LeafletMap, private readonly loadZones: TseZoneLoader) {}

  /**
   * Liga/desliga a camada. Resolve `false` se o asset falhar ao carregar.
   *
   * @example await tseZones.setVisible(true) // true quando desenhou
   */
  async setVisible(on: boolean): Promise<boolean> {
    this.visible = on;
    if (!on) { this.detach(); return true; }
    try {
      const zones = await this.ensureLoaded();
      if (this.visible) this.attach(zones);
      return true;
    } catch (error) {
      this.loading = null;
      console.warn(JSON.stringify({ event: 'explore.tse-zones.load-failed', message: String(error) }));
      return false;
    }
  }

  /** Liga (ou desliga com null) a pintura por resultado e repinta o que ja existe. */
  setResultPort(port: TseZoneResultPort | null): void {
    this.resultPort = port;
    this.outlineLayer?.setStyle((feature) => this.zoneStyle(feature as TseZoneFeature));
  }

  /** Estilo em repouso: contorno TSE + (modo 2026) preenchimento do mais votado. */
  private zoneStyle(feature: TseZoneFeature): PathOptions {
    const color = this.resultPort?.fillColor(feature.properties.nrZona) ?? null;
    return color ? { ...TSE_ZONE_STYLE, fillColor: color, fillOpacity: TSE_ZONE_RESULT_FILL_OPACITY } : { ...TSE_ZONE_STYLE };
  }

  /** Busca o asset uma unica vez (promessa memoizada). */
  private ensureLoaded(): Promise<TseZoneCollection> {
    this.loading ??= this.loadZones();
    return this.loading;
  }

  /** Cria (1a vez) e adiciona contorno + rotulos ao mapa. */
  private attach(zones: TseZoneCollection): void {
    if (!this.outlineLayer) this.build(zones);
    this.outlineLayer?.addTo(this.map);
    this.labelGroup.addTo(this.map);
    this.map.on('zoomend', this.onZoomEnd);
    this.refreshLabels();
  }

  /** Remove contorno, rotulos e o listener de zoom. */
  private detach(): void {
    if (this.outlineLayer) this.map.removeLayer(this.outlineLayer);
    this.map.removeLayer(this.labelGroup);
    this.map.off('zoomend', this.onZoomEnd);
  }

  /** Monta o pane, o L.geoJSON das zonas e os marcadores de rotulo. */
  private build(zones: TseZoneCollection): void {
    if (!this.map.getPane(TSE_ZONE_PANE)) this.map.createPane(TSE_ZONE_PANE).style.zIndex = TSE_ZONE_PANE_Z_INDEX;
    this.outlineLayer = L.geoJSON(zones, {
      pane: TSE_ZONE_PANE,
      style: (feature) => this.zoneStyle(feature as TseZoneFeature),
      onEachFeature: (feature, layer) => this.bindZone(feature as TseZoneFeature, layer as Path),
    });
    this.labels = zones.features.map((feature) => this.buildLabel(feature)).filter((l): l is TseZoneLabel => l !== null);
  }

  /** Hover realca; clique abre o popup da zona. */
  private bindZone(feature: TseZoneFeature, layer: Path): void {
    layer.on('mouseover', () => layer.setStyle(this.resultPort ? { weight: TSE_ZONE_HOVER_STYLE.weight } : TSE_ZONE_HOVER_STYLE));
    layer.on('mouseout', () => layer.setStyle(this.zoneStyle(feature)));
    layer.on('click', (event: LeafletMouseEvent) => this.openZonePopup(feature, event));
  }

  /** Abre o balao da zona no ponto clicado (mesmo visual do popup de ranking). */
  private openZonePopup(feature: TseZoneFeature, event: LeafletMouseEvent): void {
    L.DomEvent.stopPropagation(event);
    L.popup({ ...EXPLORE_TALL_POPUP_OPTIONS })
      .setLatLng(event.latlng)
      .setContent((this.resultPort?.popupBlock(feature.properties.nrZona) ?? '') + buildTseZonePopupHtml(feature.properties))
      .openOn(this.map);
  }

  /** Marcador "Nª ZE" no centroide do maior anel (null p/ geometria degenerada). */
  private buildLabel(feature: TseZoneFeature): TseZoneLabel | null {
    const centroid = polygonCentroidOf(feature.geometry);
    if (!centroid) return null;
    const icon = L.divIcon({ className: 'exp-tz-label', html: `<span>${feature.properties.nrZona}ª ZE</span>`, iconSize: [0, 0] });
    const marker = L.marker([centroid[0], centroid[1]], { icon, interactive: false, keyboard: false, pane: TSE_ZONE_PANE });
    return { marker, bounds: L.geoJSON(feature).getBounds() };
  }

  /** Mostra so os rotulos cujas zonas sao largas o bastante no zoom atual. */
  private refreshLabels(): void {
    for (const label of this.labels) {
      const show = this.zoneWidthPx(label.bounds) >= TSE_ZONE_LABEL_MIN_PX;
      if (show && !this.labelGroup.hasLayer(label.marker)) this.labelGroup.addLayer(label.marker);
      if (!show && this.labelGroup.hasLayer(label.marker)) this.labelGroup.removeLayer(label.marker);
    }
  }

  /** Largura em pixels dos bounds da zona no zoom atual. */
  private zoneWidthPx(bounds: L.LatLngBounds): number {
    const west = this.map.latLngToContainerPoint(bounds.getSouthWest());
    const east = this.map.latLngToContainerPoint(bounds.getNorthEast());
    return Math.abs(east.x - west.x);
  }
}

/**
 * Loader de producao: fetch relativo do asset (resolve em dev e no dist/).
 *
 * @example new ExploreTseZoneLayer(map, fetchTseZones(GEO_TSE_ZONES_URL))
 */
export function fetchTseZones(url: string): TseZoneLoader {
  return async () => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`zonas eleitorais: HTTP ${response.status} em ${JSON.stringify(url)} (esperado 200)`);
    return (await response.json()) as TseZoneCollection;
  };
}
